import { useMemo } from 'react';

import { USER_LOAN_PROGRAM, USER_ROLE } from '@/config/constants';
import { useGetSubscriptionStatusQuery } from '@/apis/extended/subscriptionApi';
import { useAuth } from '@/hooks/useAuth';

export const useInvestorAccess = () => {
  const { isAdmin } = useAuth();
  const {
    data: subscriptionStatus,
    isLoading,
    error,
  } = useGetSubscriptionStatusQuery();

  const hasAccess = useMemo(() => {
    if (isAdmin) return true;
    if (!subscriptionStatus) return false;

    // Pending / denied never grant access (Wave 3 — no fake success)
    if (
      subscriptionStatus.status === 'pending' ||
      subscriptionStatus.status === 'denied'
    ) {
      return false;
    }

    if (subscriptionStatus.investorAccessGranted) {
      return true;
    }

    if (subscriptionStatus.role === USER_ROLE.ADMIN) {
      return true;
    }

    if (subscriptionStatus.userType === 'admin') {
      return true;
    }

    if (
      subscriptionStatus.userType === 'investor' &&
      subscriptionStatus.status === 'active'
    ) {
      return true;
    }

    return false;
  }, [subscriptionStatus, isAdmin]);

  const checkAccess = (loanProgram: USER_LOAN_PROGRAM): boolean => {
    // Free programs available to everyone
    const freePrograms = [
      USER_LOAN_PROGRAM.CONVENTIONAL,
      USER_LOAN_PROGRAM.FHA,
      USER_LOAN_PROGRAM.VA,
      USER_LOAN_PROGRAM.USDA,
    ];

    if (freePrograms.includes(loanProgram)) {
      return true;
    }

    // Investor programs require subscription
    const investorPrograms = [
      USER_LOAN_PROGRAM.BRIDGE,
      USER_LOAN_PROGRAM.DSCR,
      USER_LOAN_PROGRAM.COMMERCIAL,
      USER_LOAN_PROGRAM.FIX_AND_FLIP,
    ];

    if (investorPrograms.includes(loanProgram)) {
      return hasAccess;
    }

    return true;
  };

  return {
    hasAccess,
    isLoading,
    error,
    checkAccess,
    subscriptionStatus,
  };
};
